/**
 * Whether a record matches what somebody typed into a search box.
 *
 * Every search used to join the fields into one string and require each word to
 * appear *somewhere* in it. Searching for a two-word trading name returned a
 * different firm entirely: one word matched, the second matched *inside* a
 * contact's name, and a digit matched inside a phone number. Three separate
 * copies of that did the same thing, so this is the one implementation.
 *
 * A record matches when either
 *   - the whole query appears as typed — which keeps a run of digits finding a
 *     phone number by its middle, and a two-word name finding one company; or
 *   - every word starts a word in the record, so "man hosiery" finds
 *     "4 MAN HOSIERY" and no longer finds "RAMAN" in somebody's contact name.
 */
const escapeForRegExp = (v: string): string => v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export function matchesSearch(parts: Array<string | null | undefined>, rawQuery: string): boolean {
    const query = (rawQuery || '').trim().toLowerCase();
    if (!query) return true;
    const hay = parts.filter(Boolean).join(' ').toLowerCase();
    if (hay.includes(query)) return true;
    return query
        .split(/\s+/)
        .filter(Boolean)
        .every(token => new RegExp('\\b' + escapeForRegExp(token)).test(hay));
}

/**
 * Which search one shared box is editing.
 *
 * The customer screens — Today, the book, Reports — share one term, so a name
 * typed on one is still there on the next. A different kind of list (stock) has
 * its own term: a customer name searched in the book must not empty it.
 */
export type SearchScope = 'customers' | 'stock';

export const searchScopeFor = (screen: string): SearchScope =>
    screen === 'stock' ? 'stock' : 'customers';
